import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnDestroy, Output, ViewChild } from '@angular/core';
import {
  PoModalAction, PoModalComponent, PoRadioGroupOption, PoSearchFilterMode, PoSelectOption, PoTableColumn
} from '@po-ui/ng-components';
import { Observable, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { ReviewService } from '../../review.service';
import { TfvcService } from '../../../core/api/tfvc.service';
import { GitService } from '../../../core/api/git.service';
import { GitBranch, GitCommit, GitFileRevision, GitPullRequest, GitPullState, GitRepoQuery, GitRepository } from '../../../core/interfaces/git.interface';
import { NotificationServiceHM } from 'src/app/menu/notification-item/notification-item.service';
import { TfvcChangeset, TfvcFileInfo } from '../../../core/interfaces/job.interface';
import { ChangesetTableRow } from './changeset-table/changeset-table.model';
import { JiraIssueComboFilterService } from './jira-issue-combo-filter.service';
import { GitRepoComboFilterService } from './git-repo-combo-filter.service';
import { GitFileComboFilterService } from './git-file-combo-filter.service';
import { getApiErrorMessage, isServiceUnavailable, showServiceUnavailableToast } from '../../../core/utils/http-error-message.util';

/** Sentinela para "todas as branches" no filtro de Pull Requests.
 * Po-select não dispara p-change para opções com valor vazio (`''`), pois sua implementação
 * interna filtra valores falsy em onSelectChange. Usar um valor não-vazio e converter para
 * `''` apenas na chamada à API. */
const GIT_PULL_ALL_BRANCHES = '__all__';

export type ReviewType = 'comparison-file' | 'comparison-tfvc' | 'standalone' | 'issue' | 'changeset';

/**
 * Linhas das tabelas de navegação git. Carregam o objeto de origem em `source`: a tabela
 * devolve a linha marcada, e é dela que se chega ao repositório ou ao Pull Request sem
 * procurar de volta na lista por número ou nome.
 */
interface GitCommitRow {
  shortRevision: string;
  subject: string;
  kind: 'Merge' | '';
  author: string;
  committedAt: string;
  source: GitCommit;
}

interface GitRevisionRow {
  shortRevision: string;
  subject: string;
  author: string;
  committedAt: string;
  source: GitFileRevision;
  $selected?: boolean;
}

interface GitPullRow {
  number: number;
  title: string;
  branches: string;
  author: string;
  updatedAt: string;
  source: GitPullRequest;
}

type WizardStep =
  | 'origin-select'
  | 'type-select'
  | 'git-type-select'
  | 'git-repo-select'
  | 'git-comparison-select'
  | 'git-pull-select'
  | 'git-commit-select'
  | 'git-comparison-form'
  | 'comparison-select'
  | 'comparison-file-form'
  | 'comparison-tfvc-form'
  | 'standalone-form'
  | 'issue-form'
  | 'changeset-form';

type IssueInputMode = 'select' | 'type';
type GitComparisonMode = 'commitVsFile' | 'commitVsCommit';

/** Data das tabelas git: até o minuto, para caber numa linha. */
const GIT_TABLE_DATE_FORMAT = 'dd/MM/yyyy HH:mm';

@Component({
  selector: 'app-send-review-modal',
  templateUrl: './send-review-modal.component.html',
  styleUrls: ['./send-review-modal.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SendReviewModalComponent implements OnDestroy {
  @ViewChild('sendModal', { static: true }) modal!: PoModalComponent;
  @ViewChild('changesetDetailModal', { static: true }) changesetDetailModal!: PoModalComponent;
  @Output() jobSubmitted = new EventEmitter<{ jobId: string }>();

  // ── Wizard ──
  currentStep: WizardStep = 'origin-select';
  private stepHistory: WizardStep[] = [];

  // ── Origem do código ──
  /**
   * De quais origens o usuário pode pedir review, conforme GET /me/capabilities.
   *
   * A tela pergunta em vez de deduzir: antes o acesso era descoberto sondando a listagem
   * de reviews, que passa pelo gate do TFVC — e quem só trabalha em repositório git era
   * barrado da ferramenta inteira.
   */
  originAvailability = { tfvc: true, git: false };

  /** Por que o TFVC não está disponível. Vazio quando está, ou quando não se sabe. */
  tfvcUnavailableReason = '';
  loadingOrigins = false;
  /** O que revisar no ramo git. Vazio até o usuário escolher. */
  gitReviewType: 'pull' | 'commit' | 'comparison' | '' = '';

  // ── Navegação git ──

  /** Valor escolhido no combo: o fullName do repositório. */
  gitRepoValue = '';
  selectedGitRepo: GitRepository | null = null;
  /**
   * Quantos repositórios cadastrados o provedor tem. `null` enquanto carrega.
   *
   * Zero não é erro, e não pode ser tratado como um: é o estado normal de toda
   * instalação nova, agora que o review exige cadastro. Sem isto a tela abria um combo
   * vazio, sem explicação e sem saída.
   */
  gitRepoCount: number | null = null;
  loadingGitRepos = false;
  /**
   * A última resposta veio cortada pelo limite do servidor.
   *
   * A lista de repositórios é um recorte: cada item custa uma consulta de permissão ao
   * provedor Git, então o servidor devolve uma página. Sem dizer isso, a tela afirmaria
   * que aquilo é tudo o que existe, e quem não encontrasse o seu repositório concluiria
   * que não tem acesso — quando bastava digitar o nome.
   */
  gitRepoListTruncated = false;

  gitPullState: GitPullState = 'open';
  /** Branch de destino do filtro de Pull Requests. GIT_PULL_ALL_BRANCHES representa "Todas". */
  gitPullTargetBranch = GIT_PULL_ALL_BRANCHES;
  /**
   * Descarta a listagem de Pull Requests em voo quando outra começa. Trocar situação e
   * branch em sequência dispara duas consultas, e a resposta da primeira podia chegar
   * depois e pintar a tabela com o filtro antigo.
   */
  private readonly gitPullsReload$ = new Subject<void>();
  gitPullRequests: GitPullRequest[] = [];
  selectedGitPull: GitPullRequest | null = null;
  loadingGitPulls = false;

  // ── Commit avulso ──
  gitBranches: GitBranch[] = [];
  gitBranchOptions: PoSelectOption[] = [];
  selectedGitBranch = '';
  loadingGitBranches = false;
  gitCommits: GitCommit[] = [];
  selectedGitCommit: GitCommit | null = null;
  loadingGitCommits = false;

  // ── Comparação git ──
  gitComparisonMode: GitComparisonMode = 'commitVsFile';
  gitComparisonPath = '';
  /** Busca do fonte pelo nome do arquivo local em andamento. */
  loadingGitFileAutoMatch = false;
  /**
   * Resultado da busca pelo nome do arquivo local quando ela não preenche o caminho.
   * Sem isto, "não achou" e "achou vários" ficavam iguais a nada ter acontecido.
   */
  gitFileAutoMatchHint = '';
  gitComparisonRef = '';
  gitRevisionHistory: GitFileRevision[] = [];
  gitBaseRevisionRows: GitRevisionRow[] = [];
  gitFromRevisionRows: GitRevisionRow[] = [];
  gitToRevisionRows: GitRevisionRow[] = [];
  selectedGitBaseRevision: GitFileRevision | null = null;
  selectedGitFromRevision: GitFileRevision | null = null;
  selectedGitToRevision: GitFileRevision | null = null;
  loadingGitFileHistory = false;

  /**
   * Linhas das tabelas de Pull Request e commit.
   *
   * São campos, e não getters: com um getter, cada ciclo de detecção entregaria um
   * array novo e a tabela perderia a linha marcada no meio da escolha.
   */
  gitPullRows: GitPullRow[] = [];
  gitCommitRows: GitCommitRow[] = [];

  /**
   * Colunas das tabelas da jornada git.
   *
   * A coluna de texto livre — título ou mensagem — vai SEM largura: absorve a sobra. Com
   * todas fixas, a soma passava da largura útil e o po-table caía em barra horizontal.
   *
   * As fixas cabem o VALOR REAL numa linha: o número do PR no Azure DevOps tem seis casas
   * (`848559`), o autor vem com nome completo e a data com hora — sem os segundos, que não
   * ajudam a escolher e faziam a data quebrar em duas linhas. Branches vai em porcentagem:
   * o nome cresce sem limite e a tabela acompanha o modal (70% da tela), então em tela
   * larga ela ganha espaço junto com o título. Autor muito longo ainda quebra em duas
   * linhas no PR: tirar mais largura do título deixaria espremido o que se lê para escolher.
   * Nas de commit e revisão a mensagem tem folga, e o autor ganha 230px.
   */
  readonly gitPullColumns: PoTableColumn[] = [
    { property: 'number', label: 'PR', width: '90px' },
    { property: 'title', label: 'Título' },
    { property: 'branches', label: 'Branches', width: '24%' },
    { property: 'author', label: 'Autor', width: '190px' },
    { property: 'updatedAt', label: 'Atualizado em', type: 'dateTime', format: GIT_TABLE_DATE_FORMAT, width: '170px' }
  ];

  private readonly gitCommitAllColumns: PoTableColumn[] = [
    { property: 'shortRevision', label: 'Revisão', width: '100px' },
    { property: 'subject', label: 'Mensagem' },
    { property: 'kind', label: 'Tipo', width: '100px' },
    { property: 'author', label: 'Autor', width: '230px' },
    { property: 'committedAt', label: 'Data', type: 'dateTime', format: GIT_TABLE_DATE_FORMAT, width: '170px' }
  ];

  /**
   * "Tipo" só aparece quando a lista tem algum merge. O Azure DevOps não informa os pais na
   * listagem de commits, e a coluna ficava vazia em todas as linhas, tirando 100px da
   * mensagem.
   */
  gitCommitColumns: PoTableColumn[] = this.commitColumnsFor([]);

  private commitColumnsFor(rows: GitCommitRow[]): PoTableColumn[] {
    return rows.some(row => row.kind)
      ? this.gitCommitAllColumns
      : this.gitCommitAllColumns.filter(column => column.property !== 'kind');
  }

  /**
   * Modo do campo de busca do `po-table`, compartilhado por toda tabela do modal: PR,
   * commit e as três de revisão da comparação (arquivo local ou do repositório contra
   * uma revisão).
   *
   * A busca do próprio `po-table` roda só sobre o que já foi CARREGADO na tela — a API
   * continua limitada a `limit` (30 por padrão); não é substituto de paginação, é achar
   * mais rápido dentro do que já veio. O padrão da lib é `startsWith`, que não acha uma
   * palavra no MEIO do título do PR ou da mensagem do commit — por isso força `contains`.
   */
  readonly tableSearchFilterType = PoSearchFilterMode.contains;

  readonly gitRevisionColumns: PoTableColumn[] = [
    { property: 'shortRevision', label: 'Revisão', width: '100px' },
    { property: 'subject', label: 'Mensagem' },
    { property: 'author', label: 'Autor', width: '230px' },
    { property: 'committedAt', label: 'Data', type: 'dateTime', format: GIT_TABLE_DATE_FORMAT, width: '170px' }
  ];

  private tableHeightFor(rowCount: number, maxHeight: number): number | undefined {
    return rowCount > 6 ? maxHeight : undefined;
  }

  get gitPullTableHeight(): number | undefined {
    return this.tableHeightFor(this.gitPullRows.length, 320);
  }

  get gitCommitTableHeight(): number | undefined {
    return this.tableHeightFor(this.gitCommitRows.length, 340);
  }

  get gitRevisionTableHeight(): number | undefined {
    return this.tableHeightFor(this.gitRevisionHistory.length, 260);
  }

  /**
   * Passos cujo conteúdo é estreito: cards ou um campo. Nenhum com tabela.
   *
   * A largura do modal existe para as tabelas, que têm muitas colunas. Nos demais
   * passos ela só afasta o conteúdo, e uma linha de tabela ficava perdida no meio de
   * 1400px.
   *
   * Os passos de Pull Request, commit e comparação estavam aqui e não deviam: as
   * colunas deles somam mais que os 960px do modal encolhido, então a tabela ganhava
   * barra horizontal e a última coluna aparecia cortada. Tabela nunca é passo compacto.
   *
   * A lista diz quem ENCOLHE, e não quem fica largo: um passo novo que ninguém lembrar
   * de classificar continua com a largura de hoje, em vez de encolher por engano.
   */
  private readonly compactSteps: WizardStep[] = [
    'origin-select',
    'type-select',
    'comparison-select',
    'git-type-select',
    'git-comparison-select',
    'git-repo-select'
  ];

  get isCompactStep(): boolean {
    return this.compactSteps.includes(this.currentStep);
  }

  get modalSize(): 'lg' | 'xl' {
    return this.isCompactStep ? 'lg' : 'xl';
  }

  /** Erro da última listagem git, exibido no passo em vez de sumir com a lista. */
  gitListError = '';
  /** Motivo de a origem git não estar disponível, para a tela explicar em vez de só desabilitar. */
  gitUnavailableReason = '';

  /** Mapeamento step → ReviewType para reuso das validações / submit */
  reviewType: ReviewType = 'comparison-file';
  submitting = false;

  // ── TFVC / Changesets ──
  tfvcQuery = '';
  tfvcFiles: TfvcFileInfo[] = [];
  tfvcFileOptions: PoSelectOption[] = [];
  selectedTfvcPath = '';
  loadingTfvcSearch = false;
  loadingHistory = false;
  loadingMoreHistory = false;
  nextHistoryContinuationToken: string | null = null;
  historyLoadError = '';
  selectedChangesetDetail: {
    id: string;
    checkInDate: string;
    author: string;
    description: string;
  } | null = null;
  loadingDetail = false;
  changesets: TfvcChangeset[] = [];
  fromChangesetItems: ChangesetTableRow[] = [];
  toChangesetItems: ChangesetTableRow[] = [];
  fromChangeset: string | null = null;
  toChangeset: string | null = null;

  // ── Arquivo local ──
  localFile: File | null = null;
  localFileName = '';

  // ── Issue ──
  issueInputMode: IssueInputMode = 'select';
  issueKey = '';
  issueKeyTyped = '';
  /**
   * Tarefa Jira informada como CONTEXTO do review — não como assunto.
   *
   * Vale para todo tipo menos `issue`, onde a tarefa É o assunto: ali ela é obrigatória,
   * vem do seletor próprio (`issueKey`/`issueKeyTyped`) e é ela que descobre os
   * changesets a revisar. Aqui é opcional e não muda o que será revisado.
   */
  contextIssueKey = '';

  /** Falha da listagem de issues do Jira, vinda do combo por fora do PoComboFilter. */
  issueListError = '';
  // ── Changeset ──
  changesetIdInput = '';

  // ── Aviso de nome divergente (igual VS Code pickMatchingTfvcFile) ──
  tfvcFileNameMismatch = false;
  tfvcMismatchMessage = '';

  // ── Modal actions (atualizadas a cada mudança de step) ──
  confirmAction: PoModalAction = { label: 'Enviar', action: () => this.submit(), disabled: true };
  cancelAction: PoModalAction = { label: 'Cancelar', action: () => this.modal.close() };

  private readonly historyPageSize = 20;
  // Valor alto intencional: desabilita o filtro por digitação no combo, forçando
  // sempre o carregamento completo da lista via getFilteredData (busca total no servidor).
  readonly issueComboFilterMinLength = 100;
  private readonly changesetDetailIconValue = 'show-details';
  private destroy$ = new Subject<void>();
  private changesetDetailTimeoutId: number | null = null;

  readonly changesetColumns: PoTableColumn[] = [
    { property: 'id', label: 'Changeset', width: '120px' },
    { property: 'checkInDate', label: 'Data', type: 'dateTime', width: '155px' },
    { property: 'author', label: 'Autor', width: '170px' },
    { property: 'descriptionShort', label: 'Resumo', width: '430px' },
    {
      property: 'detailsAction',
      label: 'Detalhes',
      type: 'icon',
      width: '90px',
      sortable: false,
      icons: [
        {
          value: this.changesetDetailIconValue,
          icon: 'po-icon-eye',
          tooltip: 'Ver detalhes do commit',
          action: (row: ChangesetTableRow) => this.openChangesetDetail(row)
        }
      ]
    }
  ];

  readonly maxDescriptionLength = 100;
  readonly changesetDetailCloseAction: PoModalAction = {
    label: 'Fechar',
    action: () => this.closeChangesetDetail()
  };

  readonly jiraIssueComboFilterService = new JiraIssueComboFilterService(this.service);
  readonly gitRepoComboFilterService = new GitRepoComboFilterService(this.gitService);
  readonly gitFileComboFilterService = new GitFileComboFilterService(this.gitService);
  readonly issueInputModeOptions: PoRadioGroupOption[] = [
    { label: 'Selecionar issue recente', value: 'select' },
    { label: 'Digitar chave manualmente', value: 'type' }
  ];



  constructor(
    private service: ReviewService,
    private tfvcService: TfvcService,
    private gitService: GitService,
    private notification: NotificationServiceHM,
    private cdr: ChangeDetectorRef
  ) {
    // O combo de repositórios só sabe devolver opções; a falha da busca chega por aqui
    // para o passo mostrá-la, em vez de virar uma lista vazia sem explicação.
    this.gitRepoComboFilterService.notify = error => {
      this.gitListError = error;
      this.cdr.markForCheck();
    };
    // A lista cortada não é erro — é a resposta certa para um cadastro grande. Chega por
    // um canal próprio para a tela pedir um termo mais específico sem alarmar.
    this.gitRepoComboFilterService.notifyTruncated = truncated => {
      this.gitRepoListTruncated = truncated;
      this.cdr.markForCheck();
    };
    // Mesmo motivo no combo de arquivos: lista vazia e busca que falhou são estados
    // diferentes, e sem isto os dois apareceriam iguais.
    this.gitFileComboFilterService.notify = error => {
      this.gitListError = error;
      this.cdr.markForCheck();
    };
    // E no de issues do Jira, que tinha o canal declarado mas ninguém escutando: a falha
    // da listagem chegava à tela como combo vazio, lido como "você não tem issue nenhuma".
    this.jiraIssueComboFilterService.notify = error => {
      this.issueListError = error;
      this.cdr.markForCheck();
    };
  }

  get hasMoreHistory(): boolean {
    return !!this.nextHistoryContinuationToken;
  }

  // ─── Wizard navigation ─────────────────────────────────────────────────────

  open(): void {
    this.resetState();
    this.modal.open();
    this.loadOrigins();
    this.cdr.markForCheck();
  }

  /**
   * Descobre quais origens oferecer.
   *
   * Falha na consulta NÃO esconde o TFVC: o fluxo que existe hoje continua alcançável, e
   * o gate do backend segue sendo quem dá a negativa. Esconder aqui tiraria de quem tem
   * acesso um caminho que funciona.
   */
  private loadOrigins(): void {
    this.loadingOrigins = true;
    this.gitUnavailableReason = '';
    this.tfvcUnavailableReason = '';
    this.cdr.markForCheck();

    this.gitService.getCapabilities()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: caps => {
          // Sem cadastro a origem git não leva a nada: todo provedor cairia desabilitado
          // no passo seguinte. Bloquear aqui evita a entrada num ramo sem saída.
          const gitUsable = caps.sources.git && caps.sources.gitHasRepositories;
          this.originAvailability = { tfvc: caps.sources.tfvc, git: gitUsable };
          // O recado do TFVC segue a mesma regra do git: quem não alcança a origem
          // descobre aqui, e não depois de andar o wizard inteiro para o gate recusar
          // no envio. A negativa só vem de uma resposta — na falha da consulta o TFVC
          // continua oferecido, como diz o cabeçalho deste método.
          this.tfvcUnavailableReason = caps.sources.tfvc
            ? ''
            : 'Você não tem acesso ao TFVC neste ambiente.';
          if (!caps.sources.git) {
            this.gitUnavailableReason = 'O conector git não está configurado neste ambiente.';
          } else if (!caps.sources.gitHasRepositories) {
            // Recado diferente de propósito: aqui o conector existe, e o que falta é um
            // administrador liberar o repositório. Repetir "não configurado" mandaria a
            // pessoa procurar no lugar errado.
            this.gitUnavailableReason =
              'Nenhum repositório git cadastrado. Peça a um administrador para cadastrá-lo em Administração > Repositórios.';
          }
          this.loadingOrigins = false;
          this.updateActions();
          this.cdr.markForCheck();
        },
        error: () => {
          this.originAvailability = { tfvc: true, git: false };
          this.tfvcUnavailableReason = '';
          this.gitUnavailableReason = 'Não foi possível verificar o acesso ao git.';
          this.loadingOrigins = false;
          this.updateActions();
          this.cdr.markForCheck();
        }
      });
  }

  /** Passo 0: escolha da origem do código. */
  selectOrigin(origin: 'tfvc' | 'git' | 'standalone'): void {
    // O standalone não tem passo de origem a percorrer: o conteúdo vem da máquina de quem
    // pede. Vai direto ao formulário, e por isso não depende de originAvailability — nem
    // TFVC nem git precisam estar disponíveis para ele funcionar.
    if (origin === 'standalone') {
      this.resetStandaloneFlowState();
      this.reviewType = 'standalone';
      this.goTo('standalone-form');
      return;
    }
    if (origin === 'tfvc') {
      if (!this.originAvailability.tfvc) { return; }
      this.goTo('type-select');
      return;
    }
    if (!this.originAvailability.git) { return; }
    this.resetGitFlowState();
    this.goTo('git-type-select');
  }

  /** Passo 2 do ramo git: o provedor já foi escolhido, então não há carga aqui. */
  selectGitType(type: 'pull' | 'commit' | 'comparison'): void {
    this.gitReviewType = type;
    if (type === 'comparison') {
      this.resetGitRepositoryState();
      this.goTo('git-comparison-select');
      this.cdr.markForCheck();
      return;
    }
    // Pull Request e commit compartilham o repositório. Git Comparison escolhe antes
    // o subtipo, igual ao ramo TFVC, e só depois vai para o repositório.
    this.resetGitRepositoryState();
    this.goTo('git-repo-select');
    this.loadGitRepositories();
    this.cdr.markForCheck();
  }

  /** Subtipo do Git Comparison, no mesmo desenho de escolha usado no TFVC. */
  selectGitComparisonSubType(mode: GitComparisonMode): void {
    this.resetGitRepositoryState();
    this.gitReviewType = 'comparison';
    this.gitComparisonMode = mode;
    this.goTo('git-repo-select');
    this.loadGitRepositories();
    this.cdr.markForCheck();
  }

  /**
   * Carrega os cadastros do provedor para o passo saber o que dizer.
   *
   * Divide os três desfechos que o combo sozinho não distingue: carregando, nenhum
   * cadastro, e falha ao consultar.
   */
  private loadGitRepositories(): void {
    this.loadingGitRepos = true;
    this.gitRepoCount = null;
    this.gitRepoListTruncated = false;
    this.gitListError = '';
    this.cdr.markForCheck();

    this.gitRepoComboFilterService.preload()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loadingGitRepos = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe(repos => { this.gitRepoCount = repos.length; });
  }

  /**
   * Nenhum repositório cadastrado — e a jornada não tem como seguir.
   *
   * Exige `!gitRepoListTruncated`: uma primeira página vazia com mais cadastros atrás
   * dela significa que nada desta página foi autorizado, não que não há cadastro. Dizer
   * "peça a um administrador" ali mandaria a pessoa resolver o problema errado.
   */
  get hasNoRegisteredRepos(): boolean {
    return !this.loadingGitRepos && this.gitRepoCount === 0 && !this.gitRepoListTruncated && !this.gitListError;
  }

  /**
   * A lista veio cortada e a busca precisa ser mais específica.
   *
   * Não precisa excluir o erro: a busca que falha zera o corte, porque "a lista está
   * incompleta" não é afirmável sobre uma resposta que não chegou.
   */
  get showsGitRepoSearchHint(): boolean {
    return this.gitRepoListTruncated;
  }

  /**
   * Repositório escolhido no combo. O valor é o fullName; o objeto vem do serviço de
   * filtro, que guarda o que devolveu — o passo seguinte precisa de owner, name e slug.
   *
   * A busca roda no servidor, sobre o cadastro: o provedor pode hospedar milhares de
   * repositórios, mas a jornada só oferece os que um admin liberou e que a pessoa
   * alcança — e essa segunda pergunta é cara demais para ser feita sobre a lista inteira.
   */
  onGitRepoSelected(value: string): void {
    this.gitRepoValue = value;
    const repo = value ? this.gitRepoComboFilterService.repositoryFor(value) : null;
    if (!repo) {
      this.selectedGitRepo = null;
      this.updateActions();
      this.cdr.markForCheck();
      return;
    }
    // Escolha de verdade, não apenas repositório que apareceu numa busca — é o que
    // decide entrar nos recentes exibidos na próxima vez que o campo abrir vazio.
    this.gitRepoComboFilterService.noteRecentSelection(repo);
    this.selectGitRepo(repo);
  }

  /** Linha marcada na tabela de Pull Requests. Desmarcar volta a desabilitar o Enviar. */
  onGitPullRowSelected(row: GitPullRow): void {
    this.selectGitPull(row.source);
  }

  onGitPullRowUnselected(): void {
    this.selectedGitPull = null;
    this.updateActions();
    this.cdr.markForCheck();
  }

  selectGitRepo(repo: GitRepository): void {
    this.selectedGitRepo = repo;
    // A busca de arquivo é por repositório: o combo precisa do slug antes de o passo da
    // comparação abrir, e o que sobrou de outro repositório não vale mais.
    this.gitFileComboFilterService.repoSlug = repo?.slug ?? '';
    this.gitFileComboFilterService.clear();
    this.selectedGitPull = null;
    this.gitPullRequests = [];
    this.gitPullRows = [];
    if (this.gitReviewType === 'commit') {
      this.selectedGitCommit = null;
      this.gitCommitRows = [];
      this.goTo('git-commit-select');
      this.loadGitBranches('commits');
      return;
    }
    if (this.gitReviewType === 'comparison') {
      this.resetGitComparisonState(true);
      this.goTo('git-comparison-form');
      this.loadGitBranches('comparison');
      return;
    }
    this.goTo('git-pull-select');
    this.loadGitBranches('pulls');
  }

  /**
   * Branches do repositório. A escolha vem antes dos commits porque o commit a revisar
   * quase nunca está na branch padrão: quem vai revisar acabou de empurrar para a sua.
   */
  /**
   * Coordenadas do repositório escolhido, para as consultas ao provedor.
   *
   * O provedor sai do próprio repositório, e não de um passo separado da jornada: a lista
   * vem de `/repositories` — os cadastrados —, e cada linha já o traz. O passo que existia
   * antes só filtrava em memória uma lista que a tela já tinha.
   *
   * `provider` é opcional em GitRepository porque a mesma interface serve a navegação do
   * provedor no cadastro, onde ele é o parâmetro da consulta e não vem na resposta. Aqui
   * ele sempre vem; se faltar, a lista está velha e o recado é melhor que uma consulta com
   * provedor vazio, que o backend recusa com uma mensagem sobre outra coisa.
   */
  private gitRepoQuery(repo: GitRepository): GitRepoQuery | null {
    if (!repo.provider) {
      this.gitListError = 'Não foi possível identificar o provedor do repositório. Recarregue a lista.';
      return null;
    }
    return {
      provider: repo.provider,
      owner: repo.owner,
      project: repo.project,
      externalId: repo.externalId,
      name: repo.name
    };
  }

  /**
   * `then` diz o que a lista de branches destrava: os commits da branch escolhida, a
   * branch da comparação, ou o filtro por branch de destino dos Pull Requests.
   */
  loadGitBranches(then: 'commits' | 'comparison' | 'pulls' = 'commits'): void {
    const repo = this.selectedGitRepo;
    if (!repo) { return; }
    const query = this.gitRepoQuery(repo);
    if (!query) { return; }
    this.loadingGitBranches = true;
    this.gitListError = '';
    this.cdr.markForCheck();

    this.gitService
      .listBranches(query)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: branches => {
          this.gitBranches = branches;
          this.gitBranchOptions = this.buildGitBranchOptions(branches, then);
          this.loadingGitBranches = false;
          this.cdr.markForCheck();
          if (then === 'pulls') {
            this.gitPullTargetBranch = (this.selectedGitRepo?.defaultBranch || '').trim() || GIT_PULL_ALL_BRANCHES;
            this.loadGitPullRequests();
            return;
          }
          if (then === 'comparison') {
            this.applyDefaultComparisonRef(branches);
            this.updateActions();
            return;
          }
          // A primeira é a tocada mais recentemente, que é a aposta certa na maioria
          // das vezes — e evita abrir o passo com lista vazia esperando uma escolha.
          this.selectedGitBranch = branches.length > 0 ? branches[0].name : '';
          if (this.selectedGitBranch) {
            this.loadGitCommits();
          } else {
            this.updateActions();
          }
        },
        error: err => {
          this.gitBranches = [];
          this.gitBranchOptions = then === 'commits' ? [] : this.buildGitBranchOptions([], then);
          this.loadingGitBranches = false;
          // Sem branches, os Pull Requests ainda se listam — só sem o filtro.
          if (then === 'pulls') {
            this.gitPullTargetBranch = (this.selectedGitRepo?.defaultBranch || '').trim() || GIT_PULL_ALL_BRANCHES;
            this.loadGitPullRequests();
            return;
          }
          this.gitListError = getApiErrorMessage(err, 'Não foi possível listar as branches.');
          this.updateActions();
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Resolve a branch da comparação assim que a lista chega, em vez de deixar o campo vazio.
   *
   * A busca do fonte é POR BRANCH — quem digita o nome antes de escolher está procurando na
   * padrão. Enquanto o campo mostrava branco, isso não aparecia em lugar nenhum: um fonte
   * que só existe na branch de trabalho simplesmente não era encontrado, e a tela dizia o
   * mesmo que diria se ele não existisse.
   *
   * Prefere a padrão declarada no cadastro, e só se ela estiver na lista — um cadastro com
   * a branch errada apontaria para algo que o provedor não tem. Sem ela, cai na primeira,
   * que é a tocada mais recentemente.
   */
  private applyDefaultComparisonRef(branches: GitBranch[]): void {
    if (this.gitComparisonRef.trim() || branches.length === 0) { return; }

    const escolhida = this.declaredDefaultBranch(branches) || branches[0].name;

    this.gitComparisonRef = escolhida;
    this.gitFileComboFilterService.ref = escolhida;
    // Um arquivo local escolhido antes de as branches chegarem teve a busca descartada
    // pela troca de branch.
    this.autoMatchGitComparisonPath();
  }

  /**
   * A branch padrão do cadastro, só se ela estiver na lista do provedor — um cadastro com
   * a branch errada apontaria para algo que o provedor não tem. Vazio quando não há.
   */
  private declaredDefaultBranch(branches: GitBranch[]): string {
    const declarada = (this.selectedGitRepo?.defaultBranch || '').trim();
    return declarada && branches.some(b => b.name === declarada) ? declarada : '';
  }

  /**
   * Opções do filtro por branch de destino. A lista do provedor traz as 50 tocadas mais
   * recentemente, quase todas de trabalho — origem, nunca destino —, e a padrão podia
   * ficar de fora, sem ter como ser escolhida. Ela entra sempre, logo depois de "Todas":
   * é o destino mais provável. Diferente da comparação, aqui um cadastro com a branch
   * errada não engana ninguém: a tabela vem vazia e "Todas" está ao lado.
   */
  private buildGitPullBranchOptions(options: PoSelectOption[]): PoSelectOption[] {
    const padrao = (this.selectedGitRepo?.defaultBranch || '').trim();
    if (!padrao) { return [{ label: 'Todas', value: GIT_PULL_ALL_BRANCHES }, ...options]; }
    return [
      { label: 'Todas', value: GIT_PULL_ALL_BRANCHES },
      { label: `${padrao} (padrão)`, value: padrao },
      ...options.filter(o => o.value !== padrao)
    ];
  }

  private buildGitBranchOptions(branches: GitBranch[], then: 'commits' | 'comparison' | 'pulls'): PoSelectOption[] {
    const options = branches.map(b => ({ label: b.name, value: b.name }));
    if (then === 'commits') { return options; }
    if (then === 'pulls') { return this.buildGitPullBranchOptions(options); }
    const label = this.selectedGitRepo?.defaultBranch
      ? `Branch padrão (${this.selectedGitRepo.defaultBranch})`
      : 'Branch padrão';
    return [{ label, value: '' }, ...options];
  }

  onGitBranchChange(branch: string | number): void {
    this.selectedGitBranch = String(branch ?? '');
    this.selectedGitCommit = null;
    this.gitCommitRows = [];
    this.updateActions();
    this.loadGitCommits();
  }

  loadGitCommits(): void {
    const repo = this.selectedGitRepo;
    if (!repo) { return; }
    const query = this.gitRepoQuery(repo);
    if (!query) { return; }
    this.loadingGitCommits = true;
    this.gitListError = '';
    this.cdr.markForCheck();

    this.gitService
      .listCommits(query, this.selectedGitBranch)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: commits => {
          this.gitCommits = commits;
          this.gitCommitRows = commits.map(commit => ({
            shortRevision: commit.shortRevision,
            subject: commit.subject,
            kind: commit.isMerge ? 'Merge' : '',
            author: commit.author || '',
            committedAt: commit.committedAt || '',
            source: commit
          }) as GitCommitRow);
          this.gitCommitColumns = this.commitColumnsFor(this.gitCommitRows);
          this.loadingGitCommits = false;
          this.updateActions();
          this.cdr.markForCheck();
        },
        error: err => {
          this.gitCommits = [];
          this.gitCommitRows = [];
          this.gitListError = getApiErrorMessage(err, 'Não foi possível listar os commits.');
          this.loadingGitCommits = false;
          this.updateActions();
          this.cdr.markForCheck();
        }
      });
  }

  /** Linha marcada na tabela de commits. Desmarcar volta a desabilitar o Enviar. */
  onGitCommitRowSelected(row: GitCommitRow): void {
    this.selectedGitCommit = row.source;
    this.updateActions();
    this.cdr.markForCheck();
  }

  onGitCommitRowUnselected(): void {
    this.selectedGitCommit = null;
    this.updateActions();
    this.cdr.markForCheck();
  }

  /**
   * Arquivo escolhido no combo de busca.
   *
   * O valor é o caminho EXATO devolvido pelo repositório, e é ele que segue para o
   * histórico e para o review. O campo era texto livre e exigia o caminho completo: um
   * diretório ou uma caixa errada devolviam "não foi possível listar o histórico", que é
   * indistinguível de o arquivo não existir.
   *
   * Escolher o arquivo já busca o histórico, que era o passo seguinte obrigatório.
   */
  onGitComparisonPathChange(value: string): void {
    this.gitComparisonPath = String(value ?? '');
    if (this.gitComparisonPath.trim()) {
      this.gitFileAutoMatchHint = '';
    }
    this.clearGitFileHistory();
    this.updateActions();
    if (this.gitComparisonPath.trim()) {
      this.loadGitFileHistory();
    }
  }

  onGitComparisonRefChange(value: string | number): void {
    this.gitComparisonRef = String(value ?? '');
    // A busca acontece por branch, então o cache de opções da branch anterior não vale
    // mais. O caminho já escolhido é PRESERVADO: descartá-lo obrigaria a refazer a escolha
    // de quem seleciona o arquivo antes da branch, e se ele não existir na branch nova o
    // histórico volta vazio, que já é a resposta correta.
    this.gitFileComboFilterService.ref = this.gitComparisonRef.trim();
    this.gitFileComboFilterService.clear();
    this.clearGitFileHistory();
    this.updateActions();
    if (this.gitComparisonPath.trim()) {
      this.loadGitFileHistory();
    } else {
      this.autoMatchGitComparisonPath();
    }
  }

  /**
   * Procura no repositório o fonte com o nome do arquivo local — o mesmo atalho do TFVC.
   *
   * O caminho só é preenchido quando a busca devolve UM arquivo com exatamente esse nome:
   * é o caminho exato do repositório, não o nome solto, então o histórico sai certo. Com
   * dois ou mais (o mesmo fonte em diretórios diferentes) a escolha fica com quem pede o
   * review, no combo. Um caminho já escolhido nunca é trocado.
   */
  private autoMatchGitComparisonPath(): void {
    const name = this.localFileName.trim();
    this.gitFileAutoMatchHint = '';
    if (!name || this.gitComparisonPath.trim()) { return; }
    const ref = this.gitComparisonRef;
    const branch = ref.trim() || 'padrão';
    this.loadingGitFileAutoMatch = true;
    this.cdr.markForCheck();

    this.gitFileComboFilterService
      .getFilteredData({ value: name })
      .pipe(takeUntil(this.destroy$))
      .subscribe(options => {
        // A resposta chegou depois de o arquivo, a branch ou o caminho mudarem: é de
        // outra pergunta.
        if (this.localFileName.trim() !== name || this.gitComparisonRef !== ref || this.gitComparisonPath.trim()) {
          return;
        }
        this.loadingGitFileAutoMatch = false;
        const exact = options.filter(option => {
          const file = this.gitFileComboFilterService.fileFor(String(option.value));
          return file?.name?.toLowerCase() === name.toLowerCase();
        });
        if (exact.length === 1) {
          this.onGitComparisonPathChange(String(exact[0].value));
        } else if (exact.length > 1) {
          this.gitFileAutoMatchHint =
            `${exact.length} arquivos com o nome ${name} na branch ${branch}. Escolha o caminho no campo.`;
        } else if (!this.gitListError) {
          this.gitFileAutoMatchHint =
            `Nenhum arquivo com o nome ${name} na branch ${branch} deste repositório.`;
        }
        this.cdr.markForCheck();
      });
  }

  loadGitFileHistory(): void {
    const repo = this.selectedGitRepo;
    const path = this.normalizedGitComparisonPath();
    if (!repo) {
      this.notification.showToast('Selecione o repositório', 'warning');
      return;
    }
    if (!path) {
      this.notification.showToast('Informe o caminho do arquivo no repositório', 'warning');
      return;
    }
    if (this.loadingGitFileHistory) { return; }

    this.loadingGitFileHistory = true;
    this.gitListError = '';
    this.gitRevisionHistory = [];
    this.gitBaseRevisionRows = [];
    this.gitFromRevisionRows = [];
    this.gitToRevisionRows = [];
    this.selectedGitBaseRevision = null;
    this.selectedGitFromRevision = null;
    this.selectedGitToRevision = null;
    this.updateActions();
    this.cdr.markForCheck();

    this.gitService.listFileHistory(repo.slug, path, this.gitComparisonRef.trim(), 30)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loadingGitFileHistory = false;
          this.updateActions();
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: revisions => {
          this.gitRevisionHistory = revisions;
          this.syncGitRevisionGridSelection();
          if (this.gitBaseRevisionRows.length === 0) {
            this.gitListError = 'Nenhuma revisão encontrada para este arquivo.';
          }
        },
        error: err => {
          this.gitRevisionHistory = [];
          this.gitBaseRevisionRows = [];
          this.gitFromRevisionRows = [];
          this.gitToRevisionRows = [];
          this.gitListError = getApiErrorMessage(err, 'Não foi possível listar o histórico do arquivo.');
        }
      });
  }

  onGitBaseRevisionRowSelected(row: GitRevisionRow): void {
    this.selectedGitBaseRevision = row.source;
    this.syncGitRevisionGridSelection();
    this.updateActions();
    this.cdr.markForCheck();
  }

  onGitBaseRevisionRowUnselected(): void {
    this.selectedGitBaseRevision = null;
    this.syncGitRevisionGridSelection();
    this.updateActions();
    this.cdr.markForCheck();
  }

  onGitFromRevisionRowSelected(row: GitRevisionRow): void {
    this.selectedGitFromRevision = row.source;
    this.syncGitRevisionGridSelection();
    this.updateActions();
    this.cdr.markForCheck();
  }

  onGitFromRevisionRowUnselected(): void {
    this.selectedGitFromRevision = null;
    this.syncGitRevisionGridSelection();
    this.updateActions();
    this.cdr.markForCheck();
  }

  onGitToRevisionRowSelected(row: GitRevisionRow): void {
    this.selectedGitToRevision = row.source;
    this.syncGitRevisionGridSelection();
    this.updateActions();
    this.cdr.markForCheck();
  }

  onGitToRevisionRowUnselected(): void {
    this.selectedGitToRevision = null;
    this.syncGitRevisionGridSelection();
    this.updateActions();
    this.cdr.markForCheck();
  }

  private clearGitFileHistory(): void {
    this.gitRevisionHistory = [];
    this.gitBaseRevisionRows = [];
    this.gitFromRevisionRows = [];
    this.gitToRevisionRows = [];
    this.selectedGitBaseRevision = null;
    this.selectedGitFromRevision = null;
    this.selectedGitToRevision = null;
    this.gitListError = '';
  }

  private syncGitRevisionGridSelection(): void {
    this.gitBaseRevisionRows = this.buildGitRevisionRows(this.selectedGitBaseRevision);
    this.gitFromRevisionRows = this.buildGitRevisionRows(this.selectedGitFromRevision);
    this.gitToRevisionRows = this.buildGitRevisionRows(this.selectedGitToRevision);
  }

  private buildGitRevisionRows(selected: GitFileRevision | null): GitRevisionRow[] {
    const selectedRevision = selected?.revision || '';
    return this.gitRevisionHistory.map(revision => ({
      shortRevision: revision.shortRevision || revision.revision.slice(0, 12),
      subject: this.gitRevisionSubject(revision.message) || '(sem mensagem)',
      author: revision.authorName || revision.authorEmail || '',
      committedAt: revision.committedAt || '',
      source: revision,
      $selected: revision.revision === selectedRevision
    }) as GitRevisionRow);
  }

  private gitRevisionSubject(message: string): string {
    return String(message || '').split(/\r?\n/)[0].trim();
  }

  private gitRevisionLabel(revision: GitFileRevision): string {
    return revision.shortRevision || revision.revision.slice(0, 12);
  }

  get hasSameGitComparisonRevisions(): boolean {
    return !!this.selectedGitFromRevision
      && !!this.selectedGitToRevision
      && this.selectedGitFromRevision.revision === this.selectedGitToRevision.revision;
  }

  private normalizedGitComparisonPath(): string {
    return this.gitComparisonPath.trim().replace(/^\/+/, '');
  }

  loadGitPullRequests(): void {
    const repo = this.selectedGitRepo;
    if (!repo) { return; }
    const query = this.gitRepoQuery(repo);
    if (!query) { return; }
    this.loadingGitPulls = true;
    this.gitListError = '';
    this.cdr.markForCheck();

    this.gitPullsReload$.next();
    this.gitService
      .listPullRequests(query, this.gitPullState, this.gitPullTargetBranch === GIT_PULL_ALL_BRANCHES ? '' : this.gitPullTargetBranch)
      .pipe(takeUntil(this.gitPullsReload$), takeUntil(this.destroy$))
      .subscribe({
        next: pulls => {
          this.gitPullRequests = pulls;
          this.gitPullRows = pulls.map(pull => ({
            number: pull.number,
            title: pull.title,
            branches: pull.sourceBranch && pull.targetBranch
              ? `${pull.sourceBranch} → ${pull.targetBranch}`
              : '',
            author: pull.author || '',
            updatedAt: pull.updatedAt || '',
            source: pull
          }) as GitPullRow);
          this.loadingGitPulls = false;
          this.updateActions();
          this.cdr.markForCheck();
        },
        error: err => {
          this.gitPullRequests = [];
          this.gitPullRows = [];
          this.gitListError = getApiErrorMessage(err, 'Não foi possível listar os pull requests.');
          this.loadingGitPulls = false;
          this.updateActions();
          this.cdr.markForCheck();
        }
      });
  }

  onGitPullStateChange(state: string | number): void {
    this.gitPullState = (state === 'closed' || state === 'all') ? state : 'open';
    // A escolha anterior morre com o filtro, como em onGitBranchChange: a tabela
    // recarrega sem linha marcada, mas `selectedGitPull` sobrevivia e o validador do
    // passo continuava passando — dava para enviar um review de um PR que não está
    // mais na lista nem aparece selecionado.
    this.selectedGitPull = null;
    this.gitPullRows = [];
    this.updateActions();
    this.loadGitPullRequests();
  }

  onGitPullTargetBranchChange(branch: string | number): void {
    this.gitPullTargetBranch = String(branch ?? '');
    // Mesma regra da situação: a escolha anterior morre com o filtro.
    this.selectedGitPull = null;
    this.gitPullRows = [];
    this.updateActions();
    this.loadGitPullRequests();
  }

  selectGitPull(pull: GitPullRequest): void {
    this.selectedGitPull = pull;
    this.updateActions();
    this.cdr.markForCheck();
  }

  private resetGitFlowState(): void {
    this.gitReviewType = '';
    this.resetGitRepositoryState();
  }

  private resetGitRepositoryState(): void {
    this.gitRepoValue = '';
    this.gitRepoCount = null;
    this.gitRepoListTruncated = false;
    this.loadingGitRepos = false;
    this.gitRepoComboFilterService.reset();
    this.selectedGitRepo = null;
    this.gitPullState = 'open';
    this.gitPullTargetBranch = GIT_PULL_ALL_BRANCHES;
    this.gitPullRequests = [];
    this.gitPullRows = [];
    this.selectedGitPull = null;
    this.contextIssueKey = '';
    this.gitBranches = [];
    this.gitBranchOptions = [];
    this.selectedGitBranch = '';
    this.gitCommits = [];
    this.gitCommitRows = [];
    this.selectedGitCommit = null;
    this.resetGitComparisonState();
    this.gitListError = '';
  }

  private resetGitComparisonState(preserveMode = false): void {
    const mode = this.gitComparisonMode;
    this.gitComparisonMode = preserveMode ? mode : 'commitVsFile';
    this.gitComparisonPath = '';
    this.loadingGitFileAutoMatch = false;
    this.gitFileAutoMatchHint = '';
    this.gitComparisonRef = '';
    this.gitFileComboFilterService.ref = '';
    this.gitFileComboFilterService.clear();
    this.gitRevisionHistory = [];
    this.gitBaseRevisionRows = [];
    this.gitFromRevisionRows = [];
    this.gitToRevisionRows = [];
    this.selectedGitBaseRevision = null;
    this.selectedGitFromRevision = null;
    this.selectedGitToRevision = null;
    this.loadingGitFileHistory = false;
    this.localFile = null;
    this.localFileName = '';
  }

  private goTo(step: WizardStep): void {
    this.stepHistory.push(this.currentStep);
    this.currentStep = step;
    this.updateActions();
  }

  goBack(): void {
    const leavingStep = this.currentStep;
    const prev = this.stepHistory.pop();
    if (prev) {
      // A issue de contexto é o mesmo campo em TODOS os formulários — os três git e os
      // quatro TFVC. Limpar por caso deixava os do TFVC de fora: digitar a chave no
      // Standalone, voltar e escolher Changeset enviava a chave do review anterior.
      // Sair de qualquer passo é o bastante para descartá-la.
      this.contextIssueKey = '';
      // A limpeza depende do que se está DEIXANDO, não de onde se chega.
      //
      // Antes cada reset era condicionado a voltar para 'type-select'. Isso amarrava a
      // limpeza à posição do passo na jornada: bastou inserir a escolha de origem antes
      // dele para que voltar de um formulário caísse noutro passo e o cache
      // sobrevivesse — dados de um review vazando para o seguinte. Sair do formulário é
      // o fato que importa; para onde se volta é detalhe de navegação.
      switch (leavingStep) {
        case 'comparison-file-form':
        case 'comparison-tfvc-form':
        case 'comparison-select':
          this.resetComparisonFlowState();
          this.reviewType = 'comparison-file';
          break;
        case 'standalone-form':
          this.resetStandaloneFlowState();
          break;
        case 'issue-form':
          this.resetIssueFlowState();
          break;
        case 'changeset-form':
          this.resetChangesetFlowState();
          break;
        // A navegação git limpa por camada, na mesma regra do resto: o que se está
        // DEIXANDO é o que se limpa. Voltar do Pull Request para a lista de
        // repositórios não pode manter o PR escolhido, e voltar para a escolha de
        // provedor não pode manter o repositório de outro provedor.
        case 'git-pull-select':
          this.selectedGitPull = null;
          this.gitPullRequests = [];
          this.gitPullRows = [];
          this.gitPullState = 'open';
          this.gitPullsReload$.next();
          this.loadingGitPulls = false;
          this.gitPullTargetBranch = GIT_PULL_ALL_BRANCHES;
          this.gitBranches = [];
          this.gitBranchOptions = [];
          break;
        case 'git-commit-select':
          this.selectedGitCommit = null;
          this.gitCommits = [];
          this.gitCommitRows = [];
          this.gitBranches = [];
          this.gitBranchOptions = [];
          this.selectedGitBranch = '';
          break;
        case 'git-comparison-form':
          this.resetGitComparisonState(true);
          break;
        case 'git-repo-select':
          this.resetGitRepositoryState();
          break;
        case 'git-comparison-select':
          this.gitReviewType = '';
          this.resetGitComparisonState();
          break;
        case 'git-type-select':
          this.resetGitFlowState();
          break;
      }
      this.gitListError = '';
      this.currentStep = prev;
    } else {
      this.modal.close();
      return;
    }
    this.updateActions();
  }

  /** Passo 1: escolha do tipo principal (igual VS Code espSmartReview.sendReview QuickPick) */
  selectMainType(type: 'comparison' | 'issue' | 'changeset'): void {
    if (type === 'comparison') {
      this.resetComparisonFlowState();
      this.goTo('comparison-select');
    } else if (type === 'changeset') {
      this.resetChangesetFlowState();
      this.reviewType = 'changeset';
      this.goTo('changeset-form');
    } else {
      this.resetIssueFlowState();
      this.reviewType = 'issue';
      this.goTo('issue-form');
    }
  }

  /** Passo 2: sub-escolha de comparação (igual VS Code comparisonModePick QuickPick) */
  selectComparisonSubType(sub: 'tfvcVsFile' | 'tfvcVsTfvc'): void {
    this.resetComparisonFlowState();

    if (sub === 'tfvcVsFile') {
      this.reviewType = 'comparison-file';
      this.goTo('comparison-file-form');
    } else {
      this.reviewType = 'comparison-tfvc';
      this.goTo('comparison-tfvc-form');
    }
  }

  private readonly stepValidators: Partial<Record<WizardStep, () => boolean>> = {
    'issue-form': () => !!this.getIssueKeyValue(),
    'changeset-form': () => !!this.changesetIdInput.trim(),
    'standalone-form': () => true,
    'comparison-tfvc-form': () => !!this.fromChangeset && !!this.toChangeset,
    'comparison-file-form': () => !!this.localFile && !!this.selectedTfvcPath && !!this.fromChangeset,
    'git-pull-select': () => !!this.selectedGitRepo && !!this.selectedGitPull,
    'git-commit-select': () => !!this.selectedGitRepo && !!this.selectedGitCommit,
    'git-comparison-form': () => {
      if (!this.selectedGitRepo || !this.normalizedGitComparisonPath()) { return false; }
      if (this.gitComparisonMode === 'commitVsFile') {
        return !!this.localFile && !!this.selectedGitBaseRevision;
      }
      return !!this.selectedGitFromRevision && !!this.selectedGitToRevision && !this.hasSameGitComparisonRevisions;
    }
  };

  private isStepValid(step: WizardStep): boolean {
    const validator = this.stepValidators[step];
    return validator ? validator() : false;
  }

  private updateActions(): void {
    const disabled = this.submitting || !this.isStepValid(this.currentStep);

    this.confirmAction = {
      label: 'Enviar',
      action: () => this.submit(),
      disabled
    };
    this.cancelAction = {
      label: this.currentStep === 'origin-select' ? 'Cancelar' : '← Voltar',
      action: () => this.goBack()
    };
  }

  onChangesetIdChange(_value: string): void {
    this.updateActions();
  }

  onIssueInputModeChange(mode: string | number): void {
    this.issueInputMode = mode === 'type' ? 'type' : 'select';
    if (this.issueInputMode === 'select') {
      this.issueKeyTyped = '';
    } else {
      this.issueKey = '';
    }
    this.updateActions();
  }

  onIssueKeyChange(value: string): void {
    this.issueKey = String(value ?? '').trim();
    this.updateActions();
  }

  onIssueKeyTypedChange(value: string): void {
    this.issueKeyTyped = String(value ?? '').trim();
    this.updateActions();
  }

  onIssueSelectKeyDown(event: KeyboardEvent): void {
    const allowedKeys = ['Tab', 'Enter', 'Escape', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (event.ctrlKey || event.metaKey || event.altKey) { return; }
    if (allowedKeys.includes(event.key)) { return; }
    event.preventDefault();
  }

  onIssueSelectPaste(event: ClipboardEvent): void {
    event.preventDefault();
  }

  private extractIssueKey(raw: string): string {
    const source = String(raw || '').trim();
    if (!source) { return ''; }
    const match = source.match(/^([A-Za-z][A-Za-z0-9_]*(?:-[A-Za-z0-9_]+)*-\d+|[A-Za-z][A-Za-z0-9_]*)(?=$|[\s\[])/);
    return (match?.[1] || source).toUpperCase();
  }

  private getIssueKeyValue(): string {
    const source = this.issueInputMode === 'type' ? this.issueKeyTyped : this.issueKey;
    return this.extractIssueKey(source);
  }

  onContextIssueKeyChange(value: string): void {
    this.contextIssueKey = String(value ?? '').trim();
    this.updateActions();
  }

  private getContextIssueKeyValue(): string {
    return this.extractIssueKey(this.contextIssueKey);
  }

  // ─── TFVC ──────────────────────────────────────────────────────────────────

  /** Ordena arquivos TFVC: nome exato primeiro, depois por data (igual VS Code prioritizeByName). */
  private prioritizeByName(files: TfvcFileInfo[], preferredName?: string): TfvcFileInfo[] {
    const byDate = (a: TfvcFileInfo, b: TfvcFileInfo) =>
      new Date(b.lastCheckInDate ?? 0).getTime() - new Date(a.lastCheckInDate ?? 0).getTime();
    if (!preferredName) { return [...files].sort(byDate); }
    const lower = preferredName.toLowerCase();
    return [...files].sort((a, b) => {
      const aMatch = (a.name || '').toLowerCase() === lower ? 1 : 0;
      const bMatch = (b.name || '').toLowerCase() === lower ? 1 : 0;
      if (aMatch !== bMatch) { return bMatch - aMatch; }
      return byDate(a, b);
    });
  }

  private buildTfvcFileOptions(files: TfvcFileInfo[]): PoSelectOption[] {
    return files.map(f => ({
      label: f.path
        ? `${f.path}${f.lastCheckInDate ? '  —  ' + new Date(f.lastCheckInDate).toLocaleString('pt-BR') : ''}`
        : f.path,
      value: f.path
    }));
  }

  private checkTfvcNameMismatch(tfvcPath: string): void {
    if (this.reviewType !== 'comparison-file' || !this.localFileName) { return; }
    const tfvcFile = this.tfvcFiles.find(f => f.path === tfvcPath);
    this.tfvcFileNameMismatch = !!tfvcFile && tfvcFile.name.toLowerCase() !== this.localFileName.toLowerCase();
    this.tfvcMismatchMessage = this.tfvcFileNameMismatch
      ? `Arquivo TFVC selecionado (${tfvcFile!.name}) é diferente do arquivo local (${this.localFileName}).`
      : '';
  }

  private normalizeContinuationToken(token?: string): string | null {
    const trimmed = (token || '').trim();
    return trimmed || null;
  }

  private truncateDescription(description: string): string {
    if (!description) { return ''; }
    if (description.length <= this.maxDescriptionLength) { return description; }
    return `${description.slice(0, this.maxDescriptionLength)}...`;
  }

  private buildGridItems(changesets: TfvcChangeset[]): ChangesetTableRow[] {
    return changesets.map(cs => ({
      id: cs.id,
      checkInDate: cs.checkInDate,
      author: cs.author || '',
      descriptionShort: this.truncateDescription((cs.description || '').trim()),
      descriptionFull: (cs.description || '').trim(),
      detailsAction: this.changesetDetailIconValue
    }));
  }

  openChangesetDetail(row: ChangesetTableRow): void {
    if (!row?.id) { return; }
    this.loadingDetail = true;
    if (this.changesetDetailTimeoutId !== null) {
      clearTimeout(this.changesetDetailTimeoutId);
    }
    this.changesetDetailTimeoutId = window.setTimeout(() => {
      this.changesetDetailTimeoutId = null;
      const cs = this.changesets.find(changeset => changeset.id === row.id);
      const description = (cs?.description || row?.descriptionFull || '').trim();
      this.selectedChangesetDetail = {
        id: row.id,
        checkInDate: cs?.checkInDate || row?.checkInDate || '',
        author: cs?.author || row?.author || '-',
        description: description || 'Sem descrição informada.'
      };
      this.loadingDetail = false;
      this.changesetDetailModal.open();
      this.cdr.markForCheck();
    });
  }

  closeChangesetDetail(): void {
    this.changesetDetailModal.close();
  }

  private syncChangesetGridSelection(): void {
    this.fromChangesetItems = this.fromChangesetItems.map(item => ({
      ...item,
      $selected: this.fromChangeset === item.id
    }));
    this.toChangesetItems = this.toChangesetItems.map(item => ({
      ...item,
      $selected: this.toChangeset === item.id
    }));
  }

  private sortChangesetsByDateDesc(changesets: TfvcChangeset[]): TfvcChangeset[] {
    return [...changesets].sort(
      (a, b) => new Date(b.checkInDate).getTime() - new Date(a.checkInDate).getTime()
    );
  }

  private mergeChangesets(existing: TfvcChangeset[], incoming: TfvcChangeset[]): TfvcChangeset[] {
    const mapById = new Map<string, TfvcChangeset>();
    [...existing, ...incoming].forEach(changeset => mapById.set(changeset.id, changeset));
    return this.sortChangesetsByDateDesc(Array.from(mapById.values()));
  }

  private resetHistoryPaginationState(): void {
    this.changesets = [];
    this.fromChangesetItems = [];
    this.toChangesetItems = [];
    this.fromChangeset = null;
    this.toChangeset = null;
    this.nextHistoryContinuationToken = null;
    this.historyLoadError = '';
    this.selectedChangesetDetail = null;
    this.loadingHistory = false;
    this.loadingMoreHistory = false;
  }

  searchTfvcFiles(): void {
    const q = this.tfvcQuery.trim();
    if (!q) {
      this.notification.showToast('Informe um termo de busca', 'warning');
      return;
    }

    if (this.loadingTfvcSearch) { return; }

    this.loadingTfvcSearch = true;

    this.tfvcService.searchTfvcFiles(q)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loadingTfvcSearch = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: files => {
          this.selectedTfvcPath = '';
          this.resetHistoryPaginationState();
          this.tfvcFileNameMismatch = false;
          this.tfvcMismatchMessage = '';

          if (!files.length) {
            this.notification.showToast('Nenhum arquivo encontrado', 'warning');
            this.tfvcFiles = [];
            this.tfvcFileOptions = [];
            return;
          }
          const ordered = this.prioritizeByName(files, this.localFileName || undefined);
          this.tfvcFiles = ordered;
          this.tfvcFileOptions = this.buildTfvcFileOptions(ordered);
        },
        error: () => {
          this.notification.showToast('Erro ao buscar arquivos TFVC. Tente novamente.', 'error');
        }
      });
  }

  onTfvcFileSelect(path: string): void {
    this.selectedTfvcPath = path;
    this.resetHistoryPaginationState();
    this.tfvcFileNameMismatch = false;
    this.tfvcMismatchMessage = '';

    if (!path) { return; }

    this.checkTfvcNameMismatch(path);
    this.loadHistoryPage(path, false);
  }

  loadMoreHistory(): void {
    if (!this.selectedTfvcPath) { return; }
    if (!this.hasMoreHistory) {
      this.historyLoadError = 'Não foi possível obter continuationToken para carregar mais registros.';
      this.notification.showToast('Não foi possível carregar mais registros do histórico TFVC', 'warning');
      return;
    }
    this.loadHistoryPage(this.selectedTfvcPath, true);
  }

  onFromChangesetSelected(item: ChangesetTableRow): void {
    this.fromChangeset = item?.id ?? null;
    this.syncChangesetGridSelection();
    this.updateActions();
  }

  onFromChangesetUnselected(_item: ChangesetTableRow): void {
    this.fromChangeset = null;
    this.syncChangesetGridSelection();
    this.updateActions();
  }

  onToChangesetSelected(item: ChangesetTableRow): void {
    this.toChangeset = item?.id ?? null;
    this.syncChangesetGridSelection();
    this.updateActions();
  }

  onToChangesetUnselected(_item: ChangesetTableRow): void {
    this.toChangeset = null;
    this.syncChangesetGridSelection();
    this.updateActions();
  }

  private loadHistoryPage(path: string, append: boolean): void {
    if (this.loadingHistory || this.loadingMoreHistory) { return; }

    const continuationToken = append ? this.nextHistoryContinuationToken : null;
    if (append && !continuationToken) { return; }

    this.historyLoadError = '';
    if (append) {
      this.loadingMoreHistory = true;
    } else {
      this.loadingHistory = true;
    }

    this.tfvcService.getTfvcFileHistory(path, continuationToken ?? undefined, this.historyPageSize)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          if (append) {
            this.loadingMoreHistory = false;
          } else {
            this.loadingHistory = false;
          }
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: response => {
          const receivedChangesets = this.sortChangesetsByDateDesc(response?.changesets ?? []);
          this.changesets = append
            ? this.mergeChangesets(this.changesets, receivedChangesets)
            : receivedChangesets;

          if (this.fromChangeset && !this.changesets.some(cs => cs.id === this.fromChangeset)) {
            this.fromChangeset = null;
          }

          if (this.toChangeset && !this.changesets.some(cs => cs.id === this.toChangeset)) {
            this.toChangeset = null;
          }

          this.nextHistoryContinuationToken = this.normalizeContinuationToken(response?.nextContinuationToken);
          this.fromChangesetItems = this.buildGridItems(this.changesets);
          this.toChangesetItems = this.buildGridItems(this.changesets);
          this.syncChangesetGridSelection();
        },
        error: () => {
          this.historyLoadError = append
            ? 'Erro ao carregar mais changesets. Tente novamente.'
            : 'Erro ao buscar histórico TFVC. Tente novamente.';
        }
      });
  }

  // ─── Arquivo local ─────────────────────────────────────────────────────────

  onLocalFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.localFile = file;
    this.localFileName = file?.name ?? '';

    if (this.currentStep === 'git-comparison-form' && this.gitComparisonMode === 'commitVsFile') {
      this.updateActions();
      this.autoMatchGitComparisonPath();
      return;
    }

    // Re-ordena TFVC options com base no arquivo local (igual VS Code prioritizeByName)
    if (this.tfvcFiles.length > 0) {
      const ordered = this.prioritizeByName(this.tfvcFiles, this.localFileName || undefined);
      this.tfvcFiles = ordered;
      this.tfvcFileOptions = this.buildTfvcFileOptions(ordered);
    }

    // Re-verifica mismatch se já tem arquivo TFVC selecionado
    if (this.selectedTfvcPath) {
      this.checkTfvcNameMismatch(this.selectedTfvcPath);
    }

    // Auto-busca TFVC com o nome do arquivo (igual VS Code: pickTfvcFile(editor, preferredName))
    if (this.currentStep === 'comparison-file-form' && this.reviewType === 'comparison-file' && this.localFileName) {
      const nameWithoutExt = this.localFileName.replace(/\.[^.]+$/, '');
      this.tfvcQuery = nameWithoutExt;
      this.searchTfvcFiles();
    }
  }

  // ─── Resumo / Confirmação ──────────────────────────────────────────────────

  /**
   * Resumo do que será enviado — igual ao QuickPick "Confirmar envio" do VS Code.
   * Retorna null enquanto os dados obrigatórios não estão preenchidos.
   */
  get confirmationSummary(): { fileName?: string; pathInfo?: string; changesetInfo?: string; issueInfo?: string } | null {
    // O ramo git não passa por reviewType: aquele campo é do vocabulário do TFVC, e
    // reaproveitá-lo obrigaria a inventar um valor que nenhum submit do TFVC entende.
    if (this.currentStep === 'git-pull-select') {
      if (!this.selectedGitRepo || !this.selectedGitPull) { return null; }
      return {
        fileName: `#${this.selectedGitPull.number} — ${this.selectedGitPull.title}`,
        pathInfo: this.selectedGitRepo.fullName,
        changesetInfo: this.selectedGitPull.sourceBranch && this.selectedGitPull.targetBranch
          ? `${this.selectedGitPull.sourceBranch} → ${this.selectedGitPull.targetBranch}`
          : undefined,
        issueInfo: this.getContextIssueKeyValue() || undefined
      };
    }

    if (this.currentStep === 'git-commit-select') {
      if (!this.selectedGitRepo || !this.selectedGitCommit) { return null; }
      return {
        fileName: `${this.selectedGitCommit.shortRevision} — ${this.selectedGitCommit.subject}`,
        pathInfo: this.selectedGitRepo.fullName,
        changesetInfo: this.selectedGitBranch || undefined,
        issueInfo: this.getContextIssueKeyValue() || undefined
      };
    }

    if (this.currentStep === 'git-comparison-form') {
      if (!this.selectedGitRepo || !this.normalizedGitComparisonPath()) { return null; }
      if (this.gitComparisonMode === 'commitVsCommit') {
        if (!this.selectedGitFromRevision || !this.selectedGitToRevision || this.hasSameGitComparisonRevisions) { return null; }
        return {
          fileName: this.normalizedGitComparisonPath().split('/').pop() || this.normalizedGitComparisonPath(),
          pathInfo: `${this.selectedGitRepo.fullName}:${this.normalizedGitComparisonPath()}`,
          changesetInfo: `${this.gitRevisionLabel(this.selectedGitFromRevision)} → ${this.gitRevisionLabel(this.selectedGitToRevision)}`,
          issueInfo: this.getContextIssueKeyValue() || undefined
        };
      }
      if (!this.localFile || !this.selectedGitBaseRevision) { return null; }
      const revisionLabel = this.gitRevisionLabel(this.selectedGitBaseRevision);
      return {
        fileName: this.localFileName,
        pathInfo: `${this.selectedGitRepo.fullName}:${this.normalizedGitComparisonPath()}`,
        changesetInfo: `${revisionLabel} → local`,
        issueInfo: this.getContextIssueKeyValue() || undefined
      };
    }

    switch (this.reviewType) {
      case 'comparison-file':
        if (!this.localFile || !this.selectedTfvcPath || !this.fromChangeset) { return null; }
        return {
          fileName: this.localFileName,
          pathInfo: this.selectedTfvcPath,
          changesetInfo: `cs ${this.fromChangeset} → local`
        };
      case 'comparison-tfvc':
        if (!this.selectedTfvcPath || !this.fromChangeset || !this.toChangeset) { return null; }
        return {
          pathInfo: this.selectedTfvcPath,
          changesetInfo: `cs ${this.fromChangeset} → ${this.toChangeset}`
        };
      case 'standalone':
        if (!this.localFile) { return null; }
        return { fileName: this.localFileName };
      case 'issue':
        if (!this.getIssueKeyValue()) { return null; }
        return {
          fileName: this.getIssueKeyValue()
        };
      case 'changeset':
        if (!this.changesetIdInput.trim()) { return null; }
        return { fileName: `Changeset #${this.changesetIdInput.trim()}` };
      default:
        return null;
    }
  }

  readonly submittingText = 'Enviando';

  // ─── Submit ────────────────────────────────────────────────────────────────

  submit(): void {
    if (!this.validate()) { return; }
    this.submitting = true;
    this.updateActions();

    let obs$: Observable<{ jobId: string }>;

    if (this.currentStep === 'git-pull-select') {
      // O slug vem do cadastro. Repositório sem cadastro nem aparece nesta jornada.
      obs$ = this.service.submitPullRequestReview(
        this.selectedGitRepo!.slug,
        this.selectedGitPull!.number,
        this.getContextIssueKeyValue()
      );
      this.finishSubmit(obs$);
      return;
    }

    if (this.currentStep === 'git-commit-select') {
      // A revisão COMPLETA, e não a abreviada da tabela: é ela que identifica o review
      // e vira o ResourceID.
      obs$ = this.service.submitCommitReview(
        this.selectedGitRepo!.slug,
        this.selectedGitCommit!.revision,
        this.getContextIssueKeyValue()
      );
      this.finishSubmit(obs$);
      return;
    }

    if (this.currentStep === 'git-comparison-form') {
      if (this.gitComparisonMode === 'commitVsCommit') {
        obs$ = this.service.submitGitComparisonCommitToCommit(
          this.selectedGitRepo!.slug,
          this.normalizedGitComparisonPath(),
          this.selectedGitFromRevision!.revision,
          this.selectedGitToRevision!.revision,
          this.getContextIssueKeyValue()
        );
      } else {
        obs$ = this.service.submitGitComparisonCommitToFile(
          this.selectedGitRepo!.slug,
          this.normalizedGitComparisonPath(),
          this.selectedGitBaseRevision!.revision,
          this.localFile!,
          this.getContextIssueKeyValue()
        );
      }
      this.finishSubmit(obs$);
      return;
    }

    switch (this.reviewType) {
      case 'comparison-file':
        obs$ = this.service.submitComparisonWithFile(
          this.fromChangeset!,
          this.selectedTfvcPath,
          this.localFile!,
          this.getContextIssueKeyValue()
        );
        break;
      case 'comparison-tfvc':
        obs$ = this.service.submitComparisonTfvcToTfvc(
          this.fromChangeset!,
          this.toChangeset!,
          this.selectedTfvcPath,
          this.getContextIssueKeyValue()
        );
        break;
      case 'standalone':
        obs$ = this.service.submitStandalone(this.localFile!, this.getContextIssueKeyValue());
        break;
      case 'issue':
        obs$ = this.service.submitIssue(this.getIssueKeyValue());
        break;
      case 'changeset':
        obs$ = this.service.submitChangesetReview(this.changesetIdInput.trim(), this.getContextIssueKeyValue());
        break;
    }

    this.finishSubmit(obs$);
  }

  /** Desfecho comum a todos os tipos: sucesso fecha o modal, erro mantém o passo. */
  private finishSubmit(obs$: Observable<{ jobId: string }>): void {
    obs$.pipe(takeUntil(this.destroy$)).subscribe({
      next: result => {
        this.notification.showToast('Review enviado com sucesso!', 'success');
        this.jobSubmitted.emit(result);
        this.modal.close();
        this.submitting = false;
        this.cdr.markForCheck();
      },
      error: error => {
        this.submitting = false;
        if (isServiceUnavailable(error)) {
          showServiceUnavailableToast(this.notification, error);
        } else {
          this.notification.showToast(
            getApiErrorMessage(error, 'Não foi possível enviar o review. Tente novamente.'),
            'error'
          );
        }
        this.updateActions();
        this.cdr.markForCheck();
      }
    });
  }

  private validate(): boolean {
    // O ramo git é validado antes do switch pelo mesmo motivo do resumo: reviewType
    // descreve os tipos do TFVC, e um Pull Request cairia no default 'comparison-file',
    // reprovado por não ter arquivo local nem changeset.
    if (this.currentStep === 'git-pull-select') {
      if (!this.selectedGitRepo) {
        this.notification.showToast('Selecione o repositório', 'warning'); return false;
      }
      if (!this.selectedGitPull) {
        this.notification.showToast('Selecione o pull request', 'warning'); return false;
      }
      return true;
    }

    if (this.currentStep === 'git-commit-select') {
      if (!this.selectedGitRepo) {
        this.notification.showToast('Selecione o repositório', 'warning'); return false;
      }
      if (!this.selectedGitCommit) {
        this.notification.showToast('Selecione o commit', 'warning'); return false;
      }
      return true;
    }

    if (this.currentStep === 'git-comparison-form') {
      if (!this.selectedGitRepo) {
        this.notification.showToast('Selecione o repositório', 'warning'); return false;
      }
      if (!this.normalizedGitComparisonPath()) {
        this.notification.showToast('Informe o caminho do arquivo no repositório', 'warning'); return false;
      }
      if (this.gitComparisonMode === 'commitVsCommit') {
        if (!this.selectedGitFromRevision) {
          this.notification.showToast('Selecione o commit de origem', 'warning'); return false;
        }
        if (!this.selectedGitToRevision) {
          this.notification.showToast('Selecione o commit de destino', 'warning'); return false;
        }
        if (this.hasSameGitComparisonRevisions) {
          this.notification.showToast('Selecione commits diferentes', 'warning'); return false;
        }
        return true;
      }
      if (!this.localFile) {
        this.notification.showToast('Selecione o arquivo local', 'warning'); return false;
      }
      if (!this.selectedGitBaseRevision) {
        this.notification.showToast('Selecione a revisão de origem', 'warning'); return false;
      }
      return true;
    }

    switch (this.reviewType) {
      case 'comparison-file':
        if (!this.localFile) {
          this.notification.showToast('Selecione o arquivo local', 'warning'); return false;
        }
        if (!this.selectedTfvcPath) {
          this.notification.showToast('Selecione o arquivo TFVC', 'warning'); return false;
        }
        if (!this.fromChangeset) {
          this.notification.showToast('Selecione o changeset de origem', 'warning'); return false;
        }
        break;
      case 'comparison-tfvc':
        if (!this.selectedTfvcPath) {
          this.notification.showToast('Selecione o arquivo TFVC', 'warning'); return false;
        }
        if (!this.fromChangeset) {
          this.notification.showToast('Selecione o changeset de origem', 'warning'); return false;
        }
        if (!this.toChangeset) {
          this.notification.showToast('Selecione o changeset de destino', 'warning'); return false;
        }
        break;
      case 'standalone':
        if (!this.localFile) {
          this.notification.showToast('Selecione um arquivo para análise', 'warning'); return false;
        }
        break;
      case 'issue':
        if (!/^(?:[A-Z][A-Z0-9_]*|[A-Z][A-Z0-9_]*(?:-[A-Z0-9_]+)*-\d+)$/.test(this.getIssueKeyValue())) {
          this.notification.showToast('Chave inválida. Use PROJ-123 ou PROJ_DESC.', 'warning'); return false;
        }
        break;
      case 'changeset':
        if (!this.changesetIdInput.trim()) {
          this.notification.showToast('Informe o ID do changeset', 'warning'); return false;
        }
        break;
    }
    return true;
  }

  private resetComparisonFlowState(): void {
    this.tfvcQuery = '';
    this.tfvcFiles = [];
    this.tfvcFileOptions = [];
    this.selectedTfvcPath = '';
    this.resetHistoryPaginationState();
    this.resetStandaloneFlowState();
    this.loadingTfvcSearch = false;
    this.tfvcFileNameMismatch = false;
    this.tfvcMismatchMessage = '';
  }

  private resetStandaloneFlowState(): void {
    this.localFile = null;
    this.localFileName = '';
  }

  private resetIssueFlowState(): void {
    this.issueInputMode = 'select';
    this.issueKey = '';
    this.issueKeyTyped = '';
    this.issueListError = '';
    // O cache das issues vale por passagem pelo fluxo. Sem isto ele vivia enquanto o
    // componente do modal existisse, e uma issue criada depois da primeira abertura só
    // aparecia recarregando a página.
    this.jiraIssueComboFilterService.reset();
  }

  private resetChangesetFlowState(): void {
    this.changesetIdInput = '';
  }

  private resetState(): void {
    this.currentStep = 'origin-select';
    this.stepHistory = [];
    this.reviewType = 'comparison-file';
    this.resetComparisonFlowState();
    this.resetIssueFlowState();
    this.resetChangesetFlowState();
    this.resetGitFlowState();
    this.gitReviewType = '';
    this.submitting = false;
    this.updateActions();
  }

  ngOnDestroy(): void {
    if (this.changesetDetailTimeoutId !== null) {
      clearTimeout(this.changesetDetailTimeoutId);
      this.changesetDetailTimeoutId = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }
}
